<template>
	<div class="omapi-integrations-form" :class="addingIntegration ? 'adding' : 'editing'">
		<integrations-form-alerts />

		<div v-if="showIntro" class="omapi-integrations-form__intro">
			<p>
				{{ introduction }}
				<a :href="docUrl" title="Learn More" target="_blank">Learn More &rarr;</a>
			</p>
		</div>

		<div v-if="isOauth" class="omapi-integrations-form__oauth">
			<core-alert :type="isLegacy ? 'warn' : 'info'">
				<span v-if="oauthComplete">
					External authorization of {{ provider.name }} has been completed. Please finish filling out the form
					below to finalize the integration.
				</span>
				<span v-else-if="isLegacy"> This is a legacy integration and can not be updated. </span>
				<span v-else>
					<strong>{{ provider.name }} requires external authorization.</strong>
					You will need to register our application with {{ provider.name }} in order to proceed.
					<core-button
						style="display: block; margin: 10px 0 0"
						color="blue"
						@click="handleOauth(provider.supports.oauth)"
					>
						Register {{ provider.name }}
					</core-button>
				</span>
			</core-alert>
		</div>

		<div class="omapi-integrations-form__wrap" :class="{ 'om-dim': oauthInProgress }">
			<!--
				The MonsterLeads label needs to be hidden, not removed from the DOM – hence using
				v-show rather than v-if. In case of `renderForm`, it needs to be removed, hence v-if.
			-->
			<form
				v-show="!isMonsterleads"
				v-if="renderForm"
				class="omapi-integrations-form__field-wrap"
				@submit.prevent="handleSubmit"
			>
				<div class="omapi-integrations-form__field-wrap-label">
					<label :for="`${provider.name}-integration-name`">{{ provider.name }} Account Name</label>
				</div>
				<div class="omapi-integrations-form__field-wrap-field">
					<input
						:id="`${provider.name}-integration-name`"
						v-model="fields.name"
						type="text"
						name="name"
						:disabled="isLegacy"
						class="omapi-input"
						:placeholder="`${provider.name} Integration`"
					/>
				</div>
			</form>
			<integrations-forms-monster-leads
				v-if="isMonsterleads"
				:provider="provider"
				@update="updateFields"
				@submit="handleSubmit"
			/>
			<integrations-forms-get-response
				v-else-if="isGetresponse"
				:provider="provider"
				@update="updateFields"
				@submit="handleSubmit"
			/>
			<integrations-forms-salesforce-pardot v-else-if="isPardot" :provider="provider" />
			<integrations-forms-uncanny-automator v-else-if="isUncannyAutomator" :provider="provider" />
			<form v-else class="common-form" @submit.prevent="handleSubmit">
				<div v-for="field in visibleFields" :key="field.id">
					<integrations-form-field v-model="fields[field.id]" :field="field" :disabled="isLegacy" />
				</div>
				<input
					v-for="hiddenfield in hiddenFields"
					:key="hiddenfield.id"
					v-model="fields[hiddenfield.id]"
					type="hidden"
					:name="hiddenfield.id"
					:disabled="isLegacy"
				/>
			</form>
		</div>
		<div v-if="showCTA" class="omapi-integrations-form__cta">
			<p v-if="addingIntegration">
				<common-selectable-input
					type="checkbox"
					:checked="defaultIntegration"
					@input="defaultIntegration = !defaultIntegration"
				>
					Set as the default integration to be used for campaigns associated with this site
				</common-selectable-input>
			</p>
			<core-loading-button
				color="green"
				:disabled="!canSubmit"
				:loading="oauthInProgress || submitting"
				@click="handleSubmit"
			>
				{{ buttonTitle }}
			</core-loading-button>
			<core-button :disabled="oauthInProgress" @click="handleClose"> Cancel </core-button>
		</div>
	</div>
</template>
<script>
import { integration } from '@/integrations/mixins/integration';
import { form } from '@/integrations/mixins/form';
import { oauth } from '@/integrations/mixins/oauth';
import { createNamespacedHelpers } from 'vuex';

const { mapState, mapActions } = createNamespacedHelpers('integrations');

export default {
	mixins: [form, integration, oauth],
	props: {
		/**
		 * Provider
		 */
		provider: {
			type: Object,
			required: true,
		},
	},
	data() {
		return this.getFieldsSetup();
	},
	computed: {
		...mapState(['addingIntegration', 'activeContent']),
		canSubmit() {
			let can = !this.oauthInProgress && this.fields.name && !this.submitting;
			if (this.addingIntegration && !this.canCreate) {
				can = false;
			}
			if (!this.addingIntegration && !this.canUpdate) {
				can = false;
			}

			return can;
		},
		submitting() {
			return (
				this.$store.getters.isLoading('addIntegration') ||
				this.$store.getters.isLoading('updateIntegration') ||
				this.$store.getters.isLoading('integrations') ||
				this.$store.getters.isLoading('resetIntegrations')
			);
		},
		/**
		 * Introduction
		 *
		 * @since 2.4.0
		 *
		 * @returns {string} The introduction.
		 */
		introduction() {
			return `Please fill out all the fields below to ${this.addingIntegration ? 'add' : 'update'} your${
				this.addingIntegration ? ' new' : ' existing'
			} integration.`;
		},
		/**
		 * Slug
		 *
		 * @since 2.4.0
		 *
		 * @returns {string} The active integration slug.
		 */
		slug() {
			let slug = '';

			if (this.editingIntegration) {
				slug = this.$get('integration.provider', '');
			}

			if (this.addingIntegration && this.provider.slug) {
				slug = this.provider.slug;
			}

			slug = slug.replace(/-(featured|existing)/, '');

			return slug || '';
		},
		/**
		 * Doc URL
		 *
		 * @since 2.4.0
		 *
		 * @returns {string} The path to the doc.
		 */
		docUrl() {
			return this.$urls.utmLink(this.provider.supports.docs.url, { utm_medium: 'Integrations' });
		},
	},
	mounted() {
		this.$bus.$on('oauthComplete', this.updateFields);
	},
	beforeDestroy() {
		this.$bus.$off('oauthComplete', this.updateFields);
	},
	methods: {
		...mapActions(['addIntegration', 'updateIntegration']),
		/**
		 * Handle Button Action
		 *
		 * @since 2.4.0
		 *
		 * @returns {void}
		 */
		handleSubmit() {
			if (!this.canSubmit) {
				return;
			}

			let providerData = {};
			this.supportFields.forEach((f) => {
				providerData[f.id] = this.$get(`fields.${f.id}`, '');
			});

			const data = {
				id: this.id,
				payload: {
					name: this.fields.name,
					providerSlug: this.slug,
					providerData,
				},
				defaultIntegration: !!this.$get('defaultIntegration'),
			};

			const method = this.addingIntegration ? 'addIntegration' : 'updateIntegration';

			this[method](data);
		},
		/**
		 * Update field values.
		 *
		 * @param {object} params Oauth params.
		 *
		 * @returns {void}
		 */
		updateFields(params) {
			Object.keys(params).map((key) => {
				this.fields[key] = params[key];
			});
		},
	},
};
</script>
